// ChatHistorySidebar.jsx
import React, { useState } from 'react';
import { Search, Trash2, MessageSquare, X } from 'lucide-react';

export default function ChatHistorySidebar({
  sessions,
  activeSessionId,
  onSelectSession,
  onDeleteSession,
  isOpen,
  onClose
}) {
  const [searchQuery, setSearchQuery] = useState('');

  const filteredSessions = sessions.filter(s => {
    const q = searchQuery.toLowerCase();
    return s.title.toLowerCase().includes(q) || 
           s.messages.some(m => m.text.toLowerCase().includes(q));
  }).sort((a, b) => b.updatedAt - a.updatedAt);

  const formatDate = (ts) => {
    if (!ts) return '';
    const d = new Date(ts);
    return d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <>
      {/* Backdrop for mobile closing */}
      {isOpen && (
        <div 
          className="absolute inset-0 z-40 bg-black/20 sm:hidden rounded-xl"
          onClick={onClose}
        />
      )}
      
      {/* Sidebar Panel */}
      <div 
        className={`absolute inset-y-0 left-0 w-72 bg-slate-50 border-r border-slate-200 shadow-2xl flex flex-col z-50 transform transition-transform duration-300 ease-in-out rounded-l-xl ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="p-4 bg-gradient-to-r from-indigo-500 to-purple-500 text-white flex justify-between items-center shrink-0 rounded-tl-xl">
          <span className="font-semibold text-sm flex items-center gap-2">
            <MessageSquare size={16} /> Chat History
          </span>
          <button onClick={onClose} className="hover:bg-white/20 p-1 rounded-md transition-colors focus:outline-none">
            <X size={18} />
          </button>
        </div>

        <div className="p-3 border-b border-slate-200 shrink-0 bg-white">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-2.5 text-slate-400" />
            <input 
              type="text" 
              placeholder="Search past chats..." 
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-3 py-2 text-xs border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500 transition-shadow"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-2 space-y-1.5 scrollbar-hide">
          {filteredSessions.length === 0 ? (
            <div className="text-center text-xs text-slate-400 mt-6 flex flex-col items-center gap-2">
              <MessageSquare size={24} className="opacity-20" />
              <p>No chat history found.</p>
            </div>
          ) : (
            filteredSessions.map(session => (
              <div 
                key={session.id}
                onClick={() => {
                  onSelectSession(session.id);
                  if (window.innerWidth < 640) onClose(); // Auto-close on mobile selection
                }}
                className={`group p-3 rounded-lg cursor-pointer transition-all border ${
                  activeSessionId === session.id 
                    ? 'bg-indigo-50/80 border-indigo-200 shadow-sm' 
                    : 'bg-white border-slate-100 hover:border-slate-300 hover:shadow-sm'
                }`}
              >
                <div className="flex justify-between items-start mb-1.5">
                  <div className="text-xs font-semibold text-slate-800 truncate pr-2">
                    {session.title}
                  </div>
                  <button 
                    onClick={(e) => { e.stopPropagation(); onDeleteSession(session.id); }}
                    className="text-slate-400 hover:text-rose-500 opacity-0 group-hover:opacity-100 transition-opacity focus:outline-none"
                    title="Delete Chat"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
                <div className="text-[11px] text-slate-500 line-clamp-2 leading-relaxed mb-1.5 h-[32px]">
                  {session.messages[session.messages.length - 1]?.text || 'Started a new session'}
                </div>
                <div className="text-[9px] text-slate-400 text-right font-medium">
                  {formatDate(session.updatedAt)}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </>
  );
}